import {observer} from 'mobx-react-lite'
import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import tw from 'tailwind-styled-components'
import {logger} from 'yougile-util'
import {DataObjectId, getColumnColorHex, Hub} from 'yougile-model'
import {Items} from '@api/model/items'
import {renameAllowed} from '@client/hub-actions-allowed'
import {useBoardStore} from '@components/board/board-store'
import {ColumnColorPicker as ColorPickerBase} from '@components/column-color-picker/column-color-picker'
import {Menu} from '@components/common/base/menu'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {ErrorBoundary} from '@components/common/error-boundary/error-boundary'
import {DnDDataPayload, useDnDStore, WithDraggable} from '@components/draggable'
import {WithDroppable} from '@components/droppable'
import {noPropagate, useBoolean, useSyncEvent} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {Column as ColumnExtensions} from '@extensions/column'
import {globals} from '@extensions/globals-base'
import {BasicColumn} from './basic-column'
import {ColumnContext, ColumnStore, useColumnStore} from './column-store'
import {
  AddColumnHeader,
  BasicColumnHeader,
  MirrorColumnHeader,
  ReportColumnHeader
} from './column-title'
import {MirrorColumn} from './mirror-column'
import {ReportColumn} from './report-column'

const TwTaskGroupContainer = tw.div<{$droppable?: boolean}>`
  task-group-container
  flex
  flex-col
  h-full
  max-h-full
  min-h-full
  relative
  ${p => (p.$droppable ? 'task-group-container' : '')}
`

// Only basic column has footer, other columns must be padded
// todo: disable pad bottom for basic columns when the add task footer is added
const TwTaskGroup = tw.div<{
  $report?: boolean
  $basic?: boolean
  $padBottom?: boolean
}>`
  task-group
  relative
  w-296
  min-w-296
  max-w-296
  min-h-0
  h-fit
  max-h-full
  px-8
  flex
  flex-col
  rounded-12
  rounded-b-16
  overflow-hidden
  ${p => {
    const cx: string[] = []
    if (p.$report) {
      cx.push('pt-8', 'bg-background-solid')
    } else if (p.$basic) {
      cx.push('bg-column-bg-gray', 'border-solid', 'border-0', 'border-t-8')
    } else {
      cx.push('pt-8', 'bg-column-bg-gray')
    }
    return cx.join(' ')
  }}
`

const TwTaskGroupWrap = tw.div`
  task-group-wrap
  min-h-0
  h-fit
  max-h-full
  flex
  flex-col
`

const TwTaskListWrap = tw.div`
  relative
  overflow-hidden
  px-8
  -mx-8
  flex-1
  flex
  flex-col
`

// const TwTaskGroupFooter = tw.div`
//   flex-none
//   py-8
// `

// todo: find what color the border is and should it be different in dark mode
const TwColumnPlaceholder = tw.div`
  w-296
  h-full
  bg-background-column-placeholder
  border-dashed
  border-1
  [border-color:rgba(231,233,239,0.5)]
  [backdrop-filter:blur(8px)]
  rounded-12
`

const TwColumnColorPickerAnchor = tw.div`
  absolute
  z-1
  left-0
  right-0
  h-12
`

const ColumnColorPicker = ({columnId}: {columnId: DataObjectId}) => {
  const ref = useRef<HTMLDivElement>(null)
  const visible = useBoolean(false)
  const canRename = useMemo(() => renameAllowed(columnId), [columnId])

  return canRename ? (
    <>
      <TwColumnColorPickerAnchor ref={ref} onClick={visible.turnOn} />
      {visible.value && (
        <Dropdown
          reference={ref}
          hideArrow
          className="overflow-visible"
          placement="bottom"
          content={
            <Menu
              className="w-250"
              items={[
                {
                  id: 'colorpicker',
                  element: <ColorPickerBase columnId={columnId} />
                }
              ]}
            />
          }
          visible
          onVisibleChange={visible.turnOff}
          theme="new-menu"
        />
      )}
    </>
  ) : null
}

interface ColumnProps {
  column: Hub
  board: Hub
  style?: CSSProperties
}

export const Column = (props: ColumnProps) => {
  const [, store] = useBoardStore()
  const [columnStore] = useState(() => new ColumnStore(store, props.column))

  useEffect(() => {
    return columnStore.initialize()
  }, [])

  return (
    <ErrorBoundary
      silent
      onError={e => logger.error('column error ' + e.message)}
    >
      <ColumnContext.Provider value={columnStore}>
        <ColumnWithSourceUpdateOnDrop {...props} />
      </ColumnContext.Provider>
    </ErrorBoundary>
  )
}

const ColumnBase = observer(({board, column, style}: ColumnProps) => {
  const [state, store] = useColumnStore()
  const [boardState, boardStore] = useBoardStore()
  const [, dndStore] = useDnDStore()

  const trackChangesIds = useMemo(
    () => [
      board.id,
      column.id,
      ...(state.isMirror
        ? ColumnExtensions.getMirroredColumns(column).map(c => c.id)
        : [])
    ],
    [column.id, board.id]
  )
  useSyncEvent(
    'current',
    {filter: trackChangesIds, debounceTime: 50},
    store.syncOnUpdate,
    [trackChangesIds]
  )

  const dragging = column.id === dndStore.getStarted('column')?.columnId
  const containerClassName = cn(
    (column.deleted || dragging) && 'hidden',
    state.isMirror && 'task-list-mirror',
    !!column.data.report && 'task-group--report',
    store.hasTimetracking() && 'timetracking',
    Items.isHidden(Items.get(column.id)) && 'api-hide'
  )
  const dragDisabled = useMemo(
    () => state.groupEdit || !globals.dragAllowed(column.id),
    [state.groupEdit, column.id]
  )

  const isReport = !!column.data.report
  const isMirror = !!state.isMirror && !isReport
  const isBasic = !isMirror && !isReport

  const containerRef = useRef<HTMLDivElement>(null)
  const onDragColumnStart = useCallback(
    (payload: DnDDataPayload<'column'>) => {
      if (containerRef.current) {
        boardStore.setColumnPlaceholderHeight(
          containerRef.current.getBoundingClientRect().height
        )
      }
      boardStore.onDragColumnStart(payload)
    },
    [boardStore, containerRef]
  )

  const onNativeDragOver = useCallback(
    (e: React.DragEvent) =>
      isBasic && store.onSubtasksColumnDragOver(e, column.id),
    [store, isBasic, column.id]
  )

  const onNativeDragLeave = useCallback(
    () => isBasic && store.onSubtasksColumnDragLeave(column.id),
    [store, isBasic, column.id]
  )

  const onNativeDrop = useCallback(
    () => isBasic && store.onSubtaskColumnDrop(store.column),
    [store, isBasic, column.id]
  )

  if (dragging) {
    return (
      <TwColumnPlaceholder
        style={{...style, height: boardState.columnPlaceholderHeight}}
      />
    )
  }

  return (
    <TwTaskGroupContainer
      onDragOver={onNativeDragOver}
      onDragLeave={onNativeDragLeave}
      onDrop={onNativeDrop}
      style={style}
      $droppable={isBasic || isMirror}
    >
      <WithDroppable
        onDragEnter={drag => boardStore.onDragColumn(drag, column.id)}
        disabled={dragDisabled}
        accepts={['column']}
      >
        <WithDraggable
          data={{
            type: 'column',
            payload: {columnId: column.id, boardId: board.id}
          }}
          clickablesOnHover
          dropArea={boardState?.dropArea}
          disabled={dragDisabled}
          onDragStart={onDragColumnStart}
          onDragEnd={boardStore.onDragColumnEnd}
          scrollContainers={[{selector: '.proj-task-list', direction: 'x'}]}
          onMouseDown={noPropagate}
          trigger="middleX"
        >
          <div className="overflow-hidden">
            {isBasic && <ColumnColorPicker columnId={column.id} />}
            <TwTaskGroup
              data-test="board-columns"
              ref={containerRef}
              $report={isReport}
              $basic={isBasic}
              $padBottom={!isReport}
              className={containerClassName}
              style={store.columnStyle}
            >
              <TwTaskGroupWrap>
                {isMirror && (
                  <>
                    <MirrorColumnHeader />
                    <TwTaskListWrap>
                      <MirrorColumn />
                    </TwTaskListWrap>
                  </>
                )}

                {isReport && (
                  <>
                    <ReportColumnHeader />
                    <TwTaskListWrap>
                      <ReportColumn />
                    </TwTaskListWrap>
                  </>
                )}

                {isBasic && (
                  <>
                    <BasicColumnHeader />
                    <TwTaskListWrap>
                      <BasicColumn />
                    </TwTaskListWrap>
                  </>
                )}
              </TwTaskGroupWrap>
            </TwTaskGroup>
          </div>
        </WithDraggable>
      </WithDroppable>
    </TwTaskGroupContainer>
  )
})

export const ColumnAdd = () => {
  const borderColor = `${getColumnColorHex({hue: 1}, globals.theme)}F5`

  return (
    <TwTaskGroup style={{borderColor}} $padBottom>
      <AddColumnHeader />
    </TwTaskGroup>
  )
}

const ColumnWithSourceUpdateOnDrop = observer((props: ColumnProps) => {
  const [dndState] = useDnDStore()
  const [, store] = useColumnStore()

  useLayoutEffect(() => {
    !dndState.dragStarted &&
      dndState.draggableProps &&
      store.updateSourceColumnOnDrop(dndState.draggableProps, props.column)
  }, [dndState.dragStarted])

  return <ColumnBase {...props} />
})
